import { Field } from '@chakra-ui/react/field';
import { VisuallyHidden } from '@chakra-ui/react/visually-hidden';
import { forwardRef, type ReactNode } from 'react';

import BitkitLabel from '../BitkitLabel/BitkitLabel';

export interface BitkitFieldProps extends Omit<Field.RootProps, 'disabled' | 'invalid' | 'readOnly'> {
  badge?: ReactNode;
  children: ReactNode;
  counterText?: string;
  errorText?: ReactNode;
  helperText?: ReactNode;
  label?: string;
  /**
   * Keep the label in the accessibility tree (associated with the control) but hide it visually.
   * For fields whose purpose is already visually obvious — e.g. a search input with a magnifier icon.
   * Badge, counter and tooltip are omitted in this mode, since they are purely visual.
   */
  labelHidden?: boolean;
  optional?: boolean;
  state?: 'disabled' | 'error' | 'readOnly' | 'warning';
  tooltip?: string;
  warningText?: ReactNode;
}

const BitkitField = forwardRef<HTMLDivElement, BitkitFieldProps>((props, ref) => {
  const {
    badge,
    counterText,
    children,
    errorText,
    helperText,
    label,
    labelHidden,
    optional,
    state,
    tooltip,
    warningText,
    ...rest
  } = props;

  const isInvalid = state === 'error' || !!errorText;

  let labelNode: ReactNode = null;
  if (labelHidden) {
    // Field.Label still wires `htmlFor` to the control; VisuallyHidden only strips it from view.
    labelNode = label ? (
      <VisuallyHidden asChild>
        <Field.Label>{label}</Field.Label>
      </VisuallyHidden>
    ) : null;
  } else if (label || badge || counterText) {
    labelNode = (
      <Field.Label asChild>
        <BitkitLabel badge={badge} counterText={counterText} optional={optional} tooltip={tooltip}>
          {label}
        </BitkitLabel>
      </Field.Label>
    );
  }

  return (
    <Field.Root
      disabled={state === 'disabled'}
      invalid={isInvalid}
      readOnly={state === 'readOnly'}
      required={!optional}
      ref={ref}
      {...rest}
    >
      {labelNode}
      {children}
      {!!helperText && !errorText && !warningText && <Field.HelperText>{helperText}</Field.HelperText>}
      {!!warningText && !errorText && <Field.HelperText color="text/body">{warningText}</Field.HelperText>}
      {!!errorText && <Field.ErrorText>{errorText}</Field.ErrorText>}
    </Field.Root>
  );
});

BitkitField.displayName = 'BitkitField';

export default BitkitField;
